import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  VEHICLE_STATES, MAINTENANCE_TYPES, DOCUMENT_CATEGORIES, StatusBadge,
} from "@/lib/vehicleStatus";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft, Loader2, Plus, DollarSign, CalendarClock, Gauge, Wrench,
  FileText, Camera, ShieldCheck, ClipboardList, FolderOpen,
} from "lucide-react";

const RES_BADGE = {
  Confirmada: "bg-green-100 text-green-800",
  Pendiente: "bg-amber-100 text-amber-800",
  Activa: "bg-blue-100 text-blue-800",
  Completada: "bg-gray-200 text-gray-700",
  Cancelada: "bg-red-100 text-red-700",
};

const DOC_ICON = {
  Registro: ClipboardList, Seguro: ShieldCheck, Inspección: FileText,
  Fotos: Camera, "Otros documentos": FolderOpen,
};

function Card({ children, className = "", ...p }) {
  return <div className={`bg-white rounded-lg border border-gray-200 shadow-sm ${className}`} {...p}>{children}</div>;
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-brand-gray/50">{label}</p>
      <p className="text-sm font-semibold text-brand-graphite mt-0.5">{value || "—"}</p>
    </div>
  );
}

function Metric({ icon: Icon, label, value, accent }) {
  return (
    <Card className="p-4 flex items-center gap-3">
      <span className={`flex h-10 w-10 items-center justify-center rounded-md ${accent}`}>
        <Icon className="h-5 w-5" strokeWidth={2} />
      </span>
      <div className="leading-tight">
        <p className="text-xl font-extrabold tracking-tight text-brand-graphite">{value}</p>
        <p className="text-xs font-medium text-brand-gray/60">{label}</p>
      </div>
    </Card>
  );
}

function AddMaintenanceDialog({ vehicleId, onDone }) {
  const [open, setOpen] = useState(false);
  const empty = { date: new Date().toISOString().slice(0, 10), odometer: "", type: "Cambio de aceite",
    description: "", provider: "", cost: "", next_service: "" };
  const [f, setF] = useState(empty);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/vehicles/${vehicleId}/maintenance`, {
        ...f, odometer: Number(f.odometer || 0), cost: Number(f.cost || 0),
      });
      toast.success("Mantenimiento registrado");
      setF(empty); setOpen(false); onDone();
    } catch { toast.error("No se pudo guardar"); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="add-maintenance-button"
          className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-md bg-brand-yellow text-brand-graphite hover:bg-[#e6ac00] transition-colors">
          <Plus className="h-3.5 w-3.5" /> Registrar mantenimiento
        </button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="maintenance-dialog">
        <DialogHeader><DialogTitle>Registrar mantenimiento</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Fecha</Label>
              <Input type="date" value={f.date} data-testid="mt-date" onChange={set("date")} /></div>
            <div className="space-y-1.5"><Label>Odómetro</Label>
              <Input type="number" value={f.odometer} data-testid="mt-odometer" onChange={set("odometer")} /></div>
          </div>
          <div className="space-y-1.5"><Label>Tipo</Label>
            <Select value={f.type} onValueChange={(v) => setF((s) => ({ ...s, type: v }))}>
              <SelectTrigger data-testid="mt-type"><SelectValue /></SelectTrigger>
              <SelectContent>{MAINTENANCE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-1.5"><Label>Descripción</Label>
            <Input value={f.description} data-testid="mt-desc" onChange={set("description")} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Proveedor</Label>
              <Input value={f.provider} data-testid="mt-provider" onChange={set("provider")} /></div>
            <div className="space-y-1.5"><Label>Costo (USD)</Label>
              <Input type="number" value={f.cost} data-testid="mt-cost" onChange={set("cost")} /></div>
          </div>
          <div className="space-y-1.5"><Label>Próximo servicio</Label>
            <Input type="date" value={f.next_service} data-testid="mt-next" onChange={set("next_service")} /></div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} data-testid="mt-save"
            className="bg-brand-yellow text-brand-graphite hover:bg-[#e6ac00] font-bold">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddDocumentDialog({ vehicleId, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ category: "Registro", name: "", note: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!f.name.trim()) return toast.error("Ingresa un nombre");
    setSaving(true);
    try {
      await api.post(`/vehicles/${vehicleId}/documents`, f);
      toast.success("Documento registrado");
      setF({ category: "Registro", name: "", note: "" }); setOpen(false); onDone();
    } catch { toast.error("No se pudo guardar"); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="add-document-button"
          className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-md bg-brand-yellow text-brand-graphite hover:bg-[#e6ac00] transition-colors">
          <Plus className="h-3.5 w-3.5" /> Registrar documento
        </button>
      </DialogTrigger>
      <DialogContent data-testid="document-dialog">
        <DialogHeader><DialogTitle>Registrar documento</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1.5"><Label>Categoría</Label>
            <Select value={f.category} onValueChange={(v) => setF((s) => ({ ...s, category: v }))}>
              <SelectTrigger data-testid="doc-category"><SelectValue /></SelectTrigger>
              <SelectContent>{DOCUMENT_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-1.5"><Label>Nombre</Label>
            <Input value={f.name} data-testid="doc-name" placeholder="Póliza 2026, Registro estatal…"
              onChange={(e) => setF((s) => ({ ...s, name: e.target.value }))} /></div>
          <div className="space-y-1.5"><Label>Nota</Label>
            <Input value={f.note} data-testid="doc-note"
              onChange={(e) => setF((s) => ({ ...s, note: e.target.value }))} /></div>
          <p className="text-xs text-brand-gray/50">Nota: la carga de archivos se habilitará más adelante.</p>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} data-testid="doc-save"
            className="bg-brand-yellow text-brand-graphite hover:bg-[#e6ac00] font-bold">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [v, setV] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [maintenance, setMaintenance] = useState([]);
  const [documents, setDocuments] = useState([]);

  const loadVehicle = useCallback(() => {
    api.get(`/vehicles/${id}`).then(({ data }) => setV(data)).catch(() => setV(false));
  }, [id]);
  const loadMaint = useCallback(() => {
    api.get(`/vehicles/${id}/maintenance`).then(({ data }) => setMaintenance(data)).catch(() => {});
  }, [id]);
  const loadDocs = useCallback(() => {
    api.get(`/vehicles/${id}/documents`).then(({ data }) => setDocuments(data)).catch(() => {});
  }, [id]);

  useEffect(() => {
    loadVehicle(); loadMaint(); loadDocs();
    api.get(`/vehicles/${id}/reservations`).then(({ data }) => setReservations(data)).catch(() => {});
  }, [id, loadVehicle, loadMaint, loadDocs]);

  const changeStatus = async (status) => {
    await api.patch(`/vehicles/${id}`, { status });
    setV((cur) => ({ ...cur, status }));
    toast.success("Estado actualizado");
  };

  if (v === false) return <p className="py-20 text-center text-brand-gray/60">Vehículo no encontrado.</p>;
  if (!v) return <div className="flex items-center justify-center py-32"><Loader2 className="h-6 w-6 animate-spin text-brand-graphite" /></div>;

  return (
    <div className="space-y-5" data-testid="vehicle-detail-page">
      <button onClick={() => navigate("/fleet")} data-testid="back-to-fleet"
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-gray hover:text-brand-graphite transition-colors">
        <ArrowLeft className="h-4 w-4" /> Volver a Flota
      </button>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-brand-graphite">
            {v.make} {v.model} {v.year}
          </h1>
          <p className="text-brand-gray/70 mt-0.5 text-sm font-mono">
            {v.unit} · Placa {v.plate}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <StatusBadge status={v.status} />
          <Select value={v.status} onValueChange={changeStatus}>
            <SelectTrigger className="w-[180px] h-9" data-testid="detail-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>{VEHICLE_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <Tabs defaultValue="resumen">
        <TabsList className="bg-white border border-gray-200 shadow-sm h-auto p-1 flex-wrap">
          {["resumen", "reservas", "mantenimiento", "documentos", "historial"].map((t) => (
            <TabsTrigger key={t} value={t} data-testid={`tab-${t}`}
              className="capitalize data-[state=active]:bg-brand-yellow data-[state=active]:text-brand-graphite font-semibold">
              {t}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* RESUMEN */}
        <TabsContent value="resumen" className="space-y-4 mt-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Metric icon={DollarSign} label="Ingresos generados"
              value={`$${(v.revenue_generated || 0).toLocaleString()}`} accent="bg-brand-yellow text-brand-graphite" />
            <Metric icon={CalendarClock} label="Días alquilados" value={v.days_rented} accent="bg-brand-graphite text-brand-yellow" />
            <Metric icon={Gauge} label="Utilización" value={`${v.utilization}%`} accent="bg-blue-100 text-blue-700" />
            <Metric icon={Wrench} label="Próximo mantenimiento" value={v.next_maintenance || "—"} accent="bg-gray-200 text-brand-gray" />
          </div>
          <Card className="p-6">
            <h3 className="font-bold text-brand-graphite mb-4">Información del vehículo</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-5">
              <Field label="Unidad" value={v.unit} />
              <Field label="Marca" value={v.make} />
              <Field label="Modelo" value={v.model} />
              <Field label="Año" value={v.year} />
              <Field label="Color" value={v.color} />
              <Field label="Tipo" value={v.type} />
              <Field label="VIN" value={v.vin} />
              <Field label="Placa" value={v.plate} />
              <Field label="Placa anterior" value={v.plate_old} />
              <Field label="Odómetro" value={`${v.odometer.toLocaleString()} mi`} />
              <Field label="Estado" value={v.status} />
              <Field label="Ubicación actual" value={v.location} />
            </div>
          </Card>
          <Card className="p-6">
            <h3 className="font-bold text-brand-graphite mb-4">Datos de compra</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-5">
              <Field label="Fecha de compra" value={v.purchase_date} />
              <Field label="Monto de compra" value={v.purchase_amount ? `$${Number(v.purchase_amount).toLocaleString()}` : "—"} />
              <Field label="Forma de pago" value={v.payment_method} />
              <Field label="Cuota (lease)" value={v.lease_fee ? `$${Number(v.lease_fee).toLocaleString()}` : "—"} />
              <Field label="Detalle de pago" value={v.payment_detail} />
              <Field label="Título" value={v.title} />
            </div>
          </Card>
        </TabsContent>

        {/* RESERVAS */}
        <TabsContent value="reservas" className="mt-4">
          <Card className="overflow-hidden" data-testid="detail-reservations">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-brand-gray/50 bg-gray-50">
                    <th className="px-5 py-3 font-semibold">Cliente</th>
                    <th className="px-5 py-3 font-semibold">Recogida</th>
                    <th className="px-5 py-3 font-semibold">Devolución</th>
                    <th className="px-5 py-3 font-semibold">Estado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {reservations.map((r, i) => (
                    <tr key={i} className="hover:bg-gray-50/60">
                      <td className="px-5 py-3 font-semibold text-brand-graphite">{r.customer}</td>
                      <td className="px-5 py-3 text-brand-gray">{r.pickup}</td>
                      <td className="px-5 py-3 text-brand-gray">{r["return"]}</td>
                      <td className="px-5 py-3">
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${RES_BADGE[r.status]}`}>{r.status}</span>
                      </td>
                    </tr>
                  ))}
                  {reservations.length === 0 && (
                    <tr><td colSpan={4} className="px-5 py-8 text-center text-brand-gray/50">Sin reservas para esta unidad.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* MANTENIMIENTO */}
        <TabsContent value="mantenimiento" className="mt-4 space-y-3">
          <div className="flex justify-end"><AddMaintenanceDialog vehicleId={id} onDone={loadMaint} /></div>
          <div className="space-y-3" data-testid="detail-maintenance">
            {maintenance.map((m) => (
              <Card key={m.id} className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 rounded bg-brand-graphite text-brand-yellow text-xs font-bold">{m.type}</span>
                      <span className="text-xs text-brand-gray/60">{m.date} · {(m.odometer || 0).toLocaleString()} mi</span>
                    </div>
                    <p className="text-sm text-brand-graphite mt-2">{m.description || "—"}</p>
                    <p className="text-xs text-brand-gray/60 mt-1">
                      Proveedor: {m.provider || "—"} · Próximo servicio: {m.next_service || "—"}
                    </p>
                  </div>
                  <span className="text-lg font-extrabold text-brand-graphite whitespace-nowrap">
                    ${(m.cost || 0).toLocaleString()}
                  </span>
                </div>
              </Card>
            ))}
            {maintenance.length === 0 && (
              <Card className="p-10 text-center text-brand-gray/50 text-sm">Sin registros de mantenimiento.</Card>
            )}
          </div>
        </TabsContent>

        {/* DOCUMENTOS */}
        <TabsContent value="documentos" className="mt-4 space-y-3">
          <div className="flex justify-end"><AddDocumentDialog vehicleId={id} onDone={loadDocs} /></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="detail-documents">
            {DOCUMENT_CATEGORIES.map((cat) => {
              const Icon = DOC_ICON[cat] || FileText;
              const items = documents.filter((d) => d.category === cat);
              return (
                <Card key={cat} className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Icon className="h-4 w-4 text-brand-graphite" />
                    <h3 className="font-bold text-brand-graphite text-sm">{cat}</h3>
                    <span className="ml-auto text-xs text-brand-gray/50">{items.length}</span>
                  </div>
                  {items.length === 0 ? (
                    <p className="text-xs text-brand-gray/40">Sin documentos.</p>
                  ) : (
                    <ul className="space-y-1.5">
                      {items.map((d) => (
                        <li key={d.id} className="text-sm text-brand-graphite flex items-center gap-2">
                          <FileText className="h-3.5 w-3.5 text-brand-gray/40" />
                          {d.name}{d.note ? <span className="text-brand-gray/50 text-xs">· {d.note}</span> : null}
                        </li>
                      ))}
                    </ul>
                  )}
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* HISTORIAL */}
        <TabsContent value="historial" className="mt-4">
          <Card className="p-5" data-testid="detail-history">
            <h3 className="font-bold text-brand-graphite mb-4">Historial de actividad</h3>
            <ul className="space-y-4">
              {[...maintenance.map((m) => ({ date: m.date, text: `Mantenimiento — ${m.type}`, sub: m.description })),
                ...reservations.map((r) => ({ date: r.pickup, text: `Reserva — ${r.customer}`, sub: r.status }))]
                .map((ev, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="mt-1 h-2.5 w-2.5 rounded-full bg-brand-yellow shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-brand-graphite">{ev.text}</p>
                      <p className="text-xs text-brand-gray/60">{ev.date} · {ev.sub}</p>
                    </div>
                  </li>
                ))}
              {maintenance.length === 0 && reservations.length === 0 && (
                <p className="text-sm text-brand-gray/50">Sin actividad registrada.</p>
              )}
            </ul>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
